import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useState,
} from "react";
import { Stepper, react, TextBox, useRefWithSetter } from "uikit";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import PaymentSystem, {
	PaymentSystemFrontEndType,
} from "../../../../../../services/PaymentSystem";
import {
	StyledColumn,
	StyledRow,
	StyledP,
} from "../../../../../../components/common";
import translationPath from "../../constants/translationPath";
import ValueStepper from "../ValueStepper";

import InternalController from "./Controller";
import BogPaymentsCurrency from "./components/BogPaymentsCurrency";
import BogPaymentsCountryIso from "./components/BogPaymentsCountryIso";

const BogPaymentBase = react.withController<
	BogPayment.PropsBase,
	BogPayment.Controller
>(({ value, onChange, controller }) => {
	const { t } = useTranslation();

	const [countryIsoRef, setCountryIsoRef] =
		useRefWithSetter<BogPaymentsCountryIso.Ref | null>(null);
	const [currencyRef, setCurrencyRef] =
		useRefWithSetter<BogPaymentsCurrency.Ref | null>(null);

	const valueEditor = useObjectEditor(value, onChange);

	const type = valueEditor.useGetter("type");

	const isExecutor = useMemo(
		() =>
			PaymentSystemFrontEndType.EXECUTOR === type ||
			PaymentSystemFrontEndType.TEMPLATE_EXECUTOR === type,
		[type],
	);

	// systemFeeAdditionalForExecutor
	const systemFeeAdditionalForExecutorValue = valueEditor.useGetter(
		"systemFeeAdditionalForExecutor",
	);
	const setSystemFeeAdditionalForExecutorValue = valueEditor.useSetter(
		"systemFeeAdditionalForExecutor",
	);
	const handleSystemFeeAdditionalForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeeAdditionalForExecutorValue(value);
			}
		},
		[setSystemFeeAdditionalForExecutorValue],
	);

	const textSystemFeeAdditionalForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeeAdditionalForExecutor`,
				"Additional commission from the driver in UAH.",
			]),
		[t],
	);

	// systemFeePercentageForExecutor
	const systemFeePercentageForExecutorValue = valueEditor.useGetter(
		"systemFeePercentageForExecutor",
	);
	const setSystemFeePercentageForExecutorValue = valueEditor.useSetter(
		"systemFeePercentageForExecutor",
	);
	const handleSystemFeePercentageForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeePercentageForExecutorValue(value);
			}
		},
		[setSystemFeePercentageForExecutorValue],
	);

	const textSystemFeePercentageForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeePercentageForExecutor`,
				"Additional driver commission in %",
			]),
		[t],
	);

	// bogClientId
	const bogClientId = valueEditor.useGetter("bogClientId");
	const setBogClientId = valueEditor.useSetter("bogClientId");
	const [errorBogClientId, setBogClientIdError] = useState<boolean>(false);
	const handleBogClientId = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setBogClientId(value);
				setBogClientIdError(false);
			}
		},
		[setBogClientId, setBogClientIdError],
	);

	const textErrorBogClientId = useMemo(() => {
		if (!errorBogClientId) return "";
		return t([
			`${translationPath}.modal.errors.paymentsKey`,
			"Client ID not entered",
		]);
	}, [t, errorBogClientId]);

	const placeholderBogClientId = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.paymentsKey`,
				"Enter client ID",
			]),
		[t],
	);

	const textBogClientId = useMemo(
		() =>
			t([
				//  `${translationPath}.modal.text.paymentsKey`,
				"Client ID",
			]),
		[t],
	);

	// bogClientSecret
	const bogClientSecret = valueEditor.useGetter("bogClientSecret");
	const bogClientSecretSet = valueEditor.useSetter("bogClientSecret");
	const [errorBogClientSecret, setBogClientSecretError] =
		useState<boolean>(false);
	const bogClientSecretHandle = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				bogClientSecretSet(value);
				setBogClientSecretError(false);
			}
		},
		[bogClientSecretSet, setBogClientSecretError],
	);

	const textErrorBogClientSecret = useMemo(() => {
		if (!errorBogClientSecret) return "";
		return t([
			`${translationPath}.modal.errors.paymentsSecretKey`,
			"Client secret not entered",
		]);
	}, [t, errorBogClientSecret]);

	const placeholderBogClientSecret = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.paymentsSecretKey2`,
				"Enter client secret",
			]),
		[t],
	);
	const textBogClientSecret = useMemo(
		() =>
			t([
				// `${translationPath}.modal.text.paymentsSecretKey`,
				"Client Secret",
			]),
		[t],
	);

	// bogApiUrl
	const bogApiUrl = valueEditor.useGetter("bogApiUrl");
	const setBogApiUrl = valueEditor.useSetter("bogApiUrl");
	const [errorBogApiUrl, setBogApiUrlError] = useState<boolean>(false);
	const handleBogApiUrl = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setBogApiUrl(value);
				setBogApiUrlError(false);
			}
		},
		[setBogApiUrl],
	);

	const textErrorBogApiUrl = useMemo(() => {
		if (!errorBogApiUrl) return "";
		return t([
			`${translationPath}.modal.errors.maibSignatureId`,
			"API URL not entered",
		]);
	}, [t, errorBogApiUrl]);

	const placeholderBogApiUrl = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.maibSignatureId`,
				"Enter API URL",
			]),
		[t],
	);

	const textBogApiUrl = useMemo(
		() =>
			t([
				//  `${translationPath}.modal.text.paymentsKey`,
				"API URL",
			]),
		[t],
	);

	// Currency and CountryIso
	const textCurrency = useMemo(
		() => t([`${translationPath}.modal.text.currency`, "Currency"]),
		[t],
	);
	const textCountryIso = useMemo(
		() => t([`${translationPath}.modal.text.countryIso`, "Country"]),
		[t],
	);
	// controller
	controller.setContext({
		bogClientSecret,
		bogClientId,
		bogApiUrl,
		currencyRef,
		countryIsoRef,
		setBogClientSecretError,
		setBogClientIdError,
		setBogApiUrlError,
	});

	return (
		<StyledColumn gap="10px" w="100%">
			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textBogClientId} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderBogClientId}
					error={textErrorBogClientId}
					value={bogClientId}
					onChange={handleBogClientId}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textBogClientSecret} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					error={textErrorBogClientSecret}
					placeholder={placeholderBogClientSecret}
					value={bogClientSecret}
					onChange={bogClientSecretHandle}
				/>
			</StyledRow>

			{isExecutor && (
				<>
					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeePercentageForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeePercentageForExecutorValue}
							onChange={handleSystemFeePercentageForExecutor}
						/>
					</StyledRow>

					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeeAdditionalForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeeAdditionalForExecutorValue}
							onChange={handleSystemFeeAdditionalForExecutor}
						/>
					</StyledRow>
				</>
			)}

			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCurrency}:</StyledP>
				<BogPaymentsCurrency
					ref={setCurrencyRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCountryIso}:</StyledP>
				<BogPaymentsCountryIso
					ref={setCountryIsoRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
		</StyledColumn>
	);
}, InternalController);

const BogPayment = memo(BogPaymentBase);

declare namespace BogPayment {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		errors?: FieldErrors<any>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default BogPayment;
